import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import CrudForm from "../../components/common/crud/CrudForm";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import alertService from "../../utils/alertService";

const stockCategoryFormSchema = [
  {
    name: "stockCategoryName",
    label: "Category Name",
    type: "text",
    placeholder: "e.g. AC JALI",
    validation: {
      required: true,
      minLength: 2,
      maxLength: 100,
    },
  },
  {
    name: "stockCategoryShortName",
    label: "Short Name",
    type: "text",
    placeholder: "e.g. AC JALI",
    validation: {
      required: true,
      minLength: 1,
      maxLength: 50,
    },
  },
  {
    name: "isActive",
    label: "Active",
    type: "checkbox",
    initialValue: true,
  },
];

function StockCategoryModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();
  const [editValues, setEditValues] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);

  const isCreatePage = location.pathname.endsWith("/new") || location.pathname.endsWith("/create");
  const isEditPage = Boolean(id);
  const isFormPage = isCreatePage || isEditPage;
  const formMode = isEditPage ? "edit" : "create";

  // Base path for navigation within this module
  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/stock-category")) return "/settings-master/stock-category";
    return "/stock-category";
  }, [location.pathname]);

  const endpoints = useMemo(
    () => ({
      list: "/stock-category",
      create: "/stock-category",
      edit: (id) => `/stock-category/${id}`,
      update: (id) => `/stock-category/${id}`,
      delete: (id) => `/stock-category/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    isSubmitting,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "stockCategoryId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "stockCategoryName", order: "ASC" },
  });

  const tableColumns = useMemo(
    () => [
      { key: "stockCategoryName", label: "Category Name", sortable: true },
      { key: "stockCategoryShortName", label: "Short Name", sortable: true },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (!isEditPage || !id) {
      setEditValues(null);
      setFormError(null);
      return;
    }

    let isMounted = true;
    const loadEditData = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/stock-category/${id}`);
        const payload = response?.data || {};
        const data = payload?.data || payload || {};
        if (!isMounted) return;
        setEditValues(data);
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditData();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  const handleSave = async (values) => {
    setFormError(null);
    try {
      if (formMode === "edit" && id) {
        await apiClient.patch(`/stock-category/${id}`, values);
        alertService.success("Record updated successfully.");
      } else {
        await apiClient.post("/stock-category", values);
        alertService.success("Record created successfully.");
      }
      await refresh();
      navigate(basePath, { replace: true });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => navigate(`${basePath}/${encrypt(item?.stockCategoryId)}/edit`);

  if (isFormPage) {
    const notFound = formMode === "edit" && !isLoadingEdit && !editValues && !formError;

    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => navigate(basePath)}>
            Back to List
          </Button>
        </div>

        {formError ? <ErrorAlert message={formError} /> : null}
        {notFound ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">Record not found.</div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <CrudForm
              mode={formMode}
              schema={stockCategoryFormSchema}
              initialValues={formMode === "edit" ? editValues || {} : {}}
              isSubmitting={isSubmitting || isLoadingEdit}
              onSubmit={handleSave}
              onCancel={() => navigate(basePath)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        searchKey="search"
        searchPlaceholder="Search by category name or short name"
        tableScrollHeight="52vh"
        toolbarAction={<Button onClick={openCreate}>Create Category</Button>}
      />
    </div>
  );
}

export default StockCategoryModulePage;
